import React, { useEffect, useMemo, useState } from 'react'
import {
  AlertInfo,
  AlertSuccess,
  AutoComplete,
  BoxV2,
  SelectV2,
  TextInputV2,
  CheckBoxFieldv2,
} from '@labrav/react-components'
import {
  FlyoutCloudSettings,
  FlyoutOnboardingPartnershipType,
  Sections,
} from '../../../modules/flyOutOnboarding/reducer'
import { FormikProps, useFormikContext } from 'formik'
import { useStyles } from './FlyoutCompanyInfo.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleCheck,
  faCircleInfo,
  faShop,
} from '@fortawesome/pro-regular-svg-icons'
import {
  DataType,
  FormField,
} from '@labrav/react-components/lib/@types/types/forms'
import { HelpDrawer, FileUploadWithLoader, useAccountId } from '@labrav/common'
import { industryCategories } from '../../../common/utils/OnboardingDataSelect'
import { FormLockedType } from '../../../types/FormLockedType'
import { Typography } from '@mui/material'
import { DefaultNowType } from '../../../common/utils/dateTimeNow'
import { isEmpty } from 'lodash'
import { bucketName } from '../../../common/utils/constants'
import { fetchAwsCountriesList } from '../../../common/api'
import { AutoCompleteProps } from '@labrav/react-components/lib/@types/v2/components/AutoComplete'
import { AwsCountriesList } from './types'
import {
  awsCountryAllowedMessage,
  sellerCountryAdvisorySupportUrl,
} from './constants'
import { getCountryStatus } from './helpers'

export type FlyoutCompanyInfoProps = {
  companyDetails: FormikProps<Sections>
  defaultNow: DefaultNowType
}

export const FlyoutCompanyInfo: React.FC<
  FlyoutCompanyInfoProps & FormLockedType
> = ({ companyDetails, defaultNow, isFormLocked }) => {
  const { classes } = useStyles()
  const industryOptions = industryCategories.map((category: string) => ({
    label: category,
    value: category,
  }))
  const accountId = useAccountId({ product: 'flyout' }) || ''
  const { values, submitCount, setValues } =
    useFormikContext() as FormikProps<Sections>
  const { infoCollectionFormMetaData } = values

  const updateProductLogoUrl = (imageUrl: string) => {
    const updatedOnboarding = {
      ...values,
      generalInformation: {
        ...values.generalInformation,
        logoUrl: imageUrl,
      },
      infoCollectionFormMetaData: {
        ...values.infoCollectionFormMetaData,
        logoUrl: imageUrl,
      },
    }
    setValues(updatedOnboarding)
  }
  const deleteProductLogoUrl = () => {
    const updatedOnboarding = {
      ...values,
      generalInformation: {
        ...values.generalInformation,
        logoUrl: '',
      },
      infoCollectionFormMetaData: {
        ...values.infoCollectionFormMetaData,
        logoUrl: '',
      },
    }
    setValues(updatedOnboarding)
  }

  const handleCompanyNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nameValue = event.target.value as string
    const updatedOnboarding = {
      ...values,
      labraPartnerDetails: {
        ...values.labraPartnerDetails,
        companyName: nameValue,
      },
      generalInformation: {
        ...values.generalInformation,
        companyName: nameValue,
      },
      infoCollectionFormMetaData: {
        ...values.infoCollectionFormMetaData,
        companyName: nameValue,
        sellerName: nameValue,
      },
    }
    setValues(updatedOnboarding)
  }

  const handleCompanyWebsiteChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nameValue = event.target.value as string
    const updatedOnboarding = {
      ...values,
      labraPartnerDetails: {
        ...values.labraPartnerDetails,
        companyWebsite: nameValue,
      },
      infoCollectionFormMetaData: {
        ...values.infoCollectionFormMetaData,
        companyWebsite: nameValue,
      },
    }
    setValues(updatedOnboarding)
  }

  const addIndustryType: FormField = {
    title: 'Industry',
    placeHolder: 'Industry',
    id: 'labraPartnerDetails.industry',
    isRequired: false,
    humanReadableKey: 'selectIndustry',
    isReadOnly: isFormLocked as boolean,
    isCustom: false,
    defaultValue: undefined,
    dataType: 'enum' as DataType,
    options: industryOptions,
  }

  const [awsCountriesLists, setAwsCountriesList] = useState<AwsCountriesList>({
    countries: [],
  })

  const handleChange: AutoCompleteProps['onChange'] = (event, newValue) => {
    const isAllowed = getCountryStatus(
      awsCountriesLists.countries,
      newValue as string
    )
    const updatedOnboarding = {
      ...values,
      labraPartnerDetails: {
        ...values.labraPartnerDetails,
        sellerOriginCountry: newValue as string,
        isManaged: !isAllowed,
      },
      ...(!isAllowed
        ? {
            cloudSettings: {
              cloudProvider: 'AWS',
              cloudAccountId: '',
              cloudAccountName: '',
              cloudRoleArn: '',
              cloudCasS3BucketName: '',
              cloudCasSnsTopic: '',
              cloudKmsKeyArn: '',
              connectionTested: false,
            } as FlyoutCloudSettings,
          }
        : {}),
    }
    companyDetails.setValues(updatedOnboarding)
  }

  const sellerOriginCountry = useMemo(
    () => companyDetails.values.labraPartnerDetails?.sellerOriginCountry,
    [companyDetails.values]
  )

  useEffect(() => {
    fetchAwsCountriesList()
      .then(response => {
        setAwsCountriesList(response.data)
      })
      .catch(error => {
        console.error('Failed to fetch countries list:', error)
      })
  }, [])

  const allCountryNames = awsCountriesLists?.countries.map(
    country => country.country
  )

  const selectedValue = companyDetails.values?.labraPartnerDetails
    ?.sellerOriginCountry
    ? companyDetails.values.labraPartnerDetails.sellerOriginCountry
    : null

  const isAllowed = getCountryStatus(
    awsCountriesLists.countries,
    sellerOriginCountry
  )

  return (
    <BoxV2
      heading={'Company information'}
      headingIcon={
        <FontAwesomeIcon
          icon={faShop}
          style={{ width: '20px', height: '17px' }}
        />
      }
      secondaryActions={
        <HelpDrawer sectionName="flyOutCompanyDetails" headerTitle="Help" />
      }
    >
      <div className={classes.formFieldsContainer}>
        <div>
          <div className={classes.fieldsContainer}>
            <div className={classes.field}>
              <TextInputV2
                keyName="labraPartnerDetails.companyName"
                title="Company name"
                type="text"
                placeHolder=""
                value={companyDetails.values.labraPartnerDetails.companyName}
                isError={
                  !!(
                    companyDetails.errors.labraPartnerDetails?.companyName &&
                    companyDetails.touched.labraPartnerDetails?.companyName
                  )
                }
                onChange={handleCompanyNameChange}
                isReadOnly={isFormLocked as boolean}
                isRequired={false}
                errorText={
                  (!!companyDetails.touched.labraPartnerDetails?.companyName &&
                    companyDetails.errors.labraPartnerDetails?.companyName) ||
                  ''
                }
                showSpinner={false}
                onBlur={companyDetails.handleBlur}
              />
            </div>

            <div className={classes.field}>
              <TextInputV2
                keyName="labraPartnerDetails.companyWebsite"
                title="Website"
                type="text"
                placeHolder=""
                value={companyDetails.values.labraPartnerDetails.companyWebsite}
                isError={
                  !!(
                    companyDetails.errors.labraPartnerDetails?.companyWebsite &&
                    companyDetails.touched.labraPartnerDetails?.companyWebsite
                  )
                }
                onChange={handleCompanyWebsiteChange}
                isReadOnly={isFormLocked as boolean}
                isRequired={false}
                errorText={
                  (!!companyDetails.touched.labraPartnerDetails
                    ?.companyWebsite &&
                    companyDetails.errors.labraPartnerDetails
                      ?.companyWebsite) ||
                  ''
                }
                showSpinner={false}
                onBlur={companyDetails.handleBlur}
              />
            </div>
            <div className={classes.field}>
              <SelectV2
                field={{ ...addIndustryType }}
                value={companyDetails.values.labraPartnerDetails.industry}
                onChange={companyDetails.handleChange}
                onBlur={companyDetails.handleBlur}
                isError={
                  !!companyDetails.errors?.labraPartnerDetails?.industry &&
                  !!companyDetails.touched.labraPartnerDetails?.industry
                }
                errorText={
                  (!!companyDetails.touched.labraPartnerDetails?.industry &&
                    companyDetails.errors.labraPartnerDetails?.industry) ||
                  ''
                }
              />
            </div>
          </div>
        </div>

        <div className={classes.fieldsContainer}>
          <div
            className={classes.partnershipTypeContainer}
            data-testid={'input-labraPartnerDetails.partnershipType'}
          >
            <Typography className={classes.checkboxSupportText}>
              Your AWS partnership type
            </Typography>
            <div className={classes.checkboxContainer}>
              <div>
                <CheckBoxFieldv2
                  title={
                    <div className={classes.checkbox}>
                      <Typography className={classes.checkboxLabel}>
                        Independent Software Vendor (ISV)
                      </Typography>
                      <Typography className={classes.checkboxSupportText}>
                        Your product(s) can be sold directly to customers or
                        through third-party channels
                      </Typography>
                    </div>
                  }
                  keyName={'labraPartnerDetails.partnershipType.isv'}
                  disabled={true}
                  value={
                    companyDetails.values.labraPartnerDetails
                      .partnershipType[0] ===
                      FlyoutOnboardingPartnershipType.ISV.valueOf() ||
                    companyDetails.values.labraPartnerDetails
                      .partnershipType[0] ===
                      FlyoutOnboardingPartnershipType.ISV_AND_CP.valueOf()
                      ? true
                      : false
                  }
                />
              </div>
              <div>
                <CheckBoxFieldv2
                  title={
                    <div className={classes.checkbox}>
                      <Typography className={classes.checkboxLabel}>
                        Channel Partner (CP)
                      </Typography>
                      <Typography className={classes.checkboxSupportText}>
                        You partner with ISVs and sell software on behalf of an
                        ISV.
                      </Typography>
                    </div>
                  }
                  keyName={'labraPartnerDetails.partnershipType.cp'}
                  disabled={true}
                  value={
                    companyDetails.values.labraPartnerDetails
                      .partnershipType[0] ===
                      FlyoutOnboardingPartnershipType.CP ||
                    companyDetails.values.labraPartnerDetails
                      .partnershipType[0] ===
                      FlyoutOnboardingPartnershipType.ISV_AND_CP
                      ? true
                      : false
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div
          className={classes.sectionContainer}
          data-testid="file-uploader-logo"
          id="infoCollectionFormMetaData.logoUrl"
        >
          <div className={classes.textContainer}>
            <Typography
              className={classes.sectionHeading}
              data-testid="section-heading"
            >
              Provide a logo
            </Typography>
            <Typography
              className={classes.sectionSubHeading}
              data-testid="section-sub-heading"
            >
              This will be uploaded to AWS Marketplace and displayed to buyers
              at the top of the product listing page and in search results.
            </Typography>
          </div>
          <FileUploadWithLoader
            keyName={'logo-upload-company-details'}
            partnerId={accountId}
            existingFileLinks={
              isEmpty(infoCollectionFormMetaData?.logoUrl)
                ? []
                : [infoCollectionFormMetaData?.logoUrl as string]
            }
            assetType="logo_url"
            folder="company_details_logo_url"
            fileUploadDescription="The image file for your company logo must be in .png or .jpg format with a white or transparent background. Recommended image size is between 120 and 640 (either width or height) pixels with a 1:1 or 2:1 (wide) ratio"
            quantity={1}
            fileUploadType="png/jpg"
            unit="MB"
            uploadFileSize={5}
            acceptedFormats={['.png', '.jpg']}
            updateUrl={updateProductLogoUrl}
            deleteUrl={deleteProductLogoUrl}
            defaultNow={defaultNow}
            fileUploadButtonText={'Upload an image'}
            fileFormatIncorrectText={
              'Failed to upload the image. Format is incorrect.'
            }
            fileUploadContainerTitle="Drag and drop image here"
            disabled={isFormLocked}
            bucketName={bucketName}
            uploadError={
              (submitCount > 0 &&
                companyDetails.errors.infoCollectionFormMetaData?.logoUrl) ||
              ''
            }
            checkDuplicate
            showRealFileName
          />
        </div>
        <div className={classes.fieldsContainerAutoComplete}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography className={classes.contentHeading}>
              Which country is your business entity organised or incorporated
              in?
            </Typography>
            <Typography className={classes.subHeading}>
              To sell paid software on AWS Marketplace, your company must be
              incorporated in Australia, Bahrain, an EU member state, Hong Kong
              SAR, Israel, Japan, New Zealand, Norway, Qatar, Switzerland, UAE,
              UK, or US. If you don't meet this criteria, Labra can manage your
              account.
            </Typography>
          </div>
          <div className={classes.fieldWidth}>
            <AutoComplete
              id={'labraPartnerDetails.sellerOriginCountry'}
              label={'Select a country'}
              options={allCountryNames}
              value={selectedValue}
              onChange={handleChange}
              onBlur={companyDetails.handleBlur}
              error={
                !!(
                  companyDetails.errors.labraPartnerDetails
                    ?.sellerOriginCountry &&
                  companyDetails.touched.labraPartnerDetails
                    ?.sellerOriginCountry
                )
              }
              helperText={
                (companyDetails.touched.labraPartnerDetails
                  ?.sellerOriginCountry &&
                  companyDetails.errors.labraPartnerDetails
                    ?.sellerOriginCountry) ||
                ''
              }
              disabled={isFormLocked}
            />
          </div>

          {sellerOriginCountry && !isEmpty(awsCountriesLists.countries) ? (
            isAllowed ? (
              <AlertSuccess
                icon={
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    style={{ width: '20px', height: '20px' }}
                  />
                }
                infoText={awsCountryAllowedMessage}
              />
            ) : (
              <AlertInfo
                icon={
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    style={{ width: '20px', height: '20px' }}
                  />
                }
                infoText={
                  <>
                    <div style={{ overflow: 'hidden' }}>
                      <Typography className={classes.alertInfoHeading}>
                        Advisory
                      </Typography>
                      <Typography className={classes.alertInfoSubHeading}>
                        Your listing will be taken live on AWS Marketplace under
                        Labra's account as your company is not from the AWS
                        approved seller country list and cannot sell their
                        products on Marketplace.
                      </Typography>
                      <Typography className={classes.alertInfoSubHeading}>
                        Please ask your supervisor in case you are not sure what
                        this means.
                      </Typography>
                      <Typography className={classes.alertInfoLinkText}>
                        <a
                          className={classes.alertInfoLinkText}
                          href={sellerCountryAdvisorySupportUrl}
                          target="_blank"
                        >
                          Contact our customer support for further
                          clarification.
                        </a>
                      </Typography>
                    </div>
                  </>
                }
              />
            )
          ) : null}
        </div>
      </div>
    </BoxV2>
  )
}
